import { ReactNode } from 'react';

import { Button } from '@/components/ui/button';

interface CreateClearFiltersButtonParams {
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

/**
 * Creates the standard "Clear All Filters" action button for filtered empty states.
 *
 * Use this helper to ensure consistent filter clearing UX across all filterable tables.
 * The button only appears when filters are active (hasActiveFilters=true).
 *
 * @param hasActiveFilters - Whether any filters (search or advanced) are currently active
 * @param onClearFilters - Callback to reset all filters
 * @returns Button component or undefined if no active filters
 *
 * @example
 * ```tsx
 * // In a table utility (getAssetNoDataConfig, etc.)
 * return {
 *   icon: <AssetIcon />,
 *   title: 'No Assets match filter',
 *   action: createClearFiltersButton({ hasActiveFilters, onClearFilters }),
 * };
 * ```
 */
export const createClearFiltersButton = ({
  hasActiveFilters,
  onClearFilters,
}: CreateClearFiltersButtonParams): ReactNode | undefined => {
  if (!hasActiveFilters) return undefined;

  return (
    <Button
      data-testid="clear-all-filters"
      onClick={onClearFilters}
      size="sm"
    >
      Clear All Filters
    </Button>
  );
};
